import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

import { SwapApiService } from '../services/swap-api.service';
import { WalletService } from '../services/wallet.service';

type Token = {
  address: string;
  chainId: number;
  symbol?: string;
  name?: string;
  decimals?: number;
  logoURI?: string;
};

type TokensMap = Map<number, Token[]>;
type BalancesMap = Map<string, string>; // "<tokenAddrLower>_<chainId>" -> amount string

const dbg = (...args: any[]) => {
  const w = (window as any);
  const f = w?.debugLog || console.log.bind(console);
  f(...args);
};

const balKey = (chainId: number, tokenAddrLower: string) =>
  `${tokenAddrLower}_${Number(chainId)}`;

const toLowerAddr = (a?: string) => (a || '').toLowerCase();

const ZERO_ADDR = '0x0000000000000000000000000000000000000000';

@Injectable({ providedIn: 'root' })
export class TokenPortfolioCacheService {
  private tokensByChain: TokensMap = new Map();
  private tokensStamp: Map<number, number> = new Map();

  private balances: BalancesMap = new Map();
  private balancesStamp: Map<string, number> = new Map();

  private tokensBus: Map<number, BehaviorSubject<Token[]>> = new Map();
  private balancesAggBus: Map<string, BehaviorSubject<Record<string, string>>> = new Map();

  private readonly MAX_PARALLEL_BAL_CALLS = 10;

  constructor(
    private swapApi: SwapApiService,
    private wallet: WalletService,
  ) {
    dbg('🧊 [TPC] TokenPortfolioCacheService constructed');
  }

  // --- FIX 1 : compteur correct, basé sur le suffixe "_<chainId>" des clés du Map ---

  private countOwnedTokensForChain(chainId: number): number {
    const suffix = `_${Number(chainId)}`;
    let n = 0;
    for (const k of this.balances.keys()) {
      if (k.endsWith(suffix)) n++;
    }
    return n;
  }

  // Garde pour ne pas lancer plusieurs complétions en parallèle pour le même (addr, chains)
  private _bgCompletionRunning: Record<string, boolean> = {};

  private async runBackgroundCompletion(address: string, chainIds: number[]): Promise<void> {
    const addr = address.toLowerCase();
    const key = `${addr}:${chainIds.slice().sort().join(',')}`;

    if (this._bgCompletionRunning[key]) {
      return;
    }
    this._bgCompletionRunning[key] = true;

    try {
      for (const cid of chainIds) {
        let prevCount = this.countOwnedTokensForChain(cid);
        let stableRounds = 0;

        // On boucle tant qu’on découvre encore des tokens (max 2 tours sans progrès)
        while (stableRounds < 2) {
          try {
            await this.ensureBalances(addr, cid);
          } catch (e) {
            console.warn('[TPC][bg] ensureBalances failed for', cid, e);
          }

          const newCount = this.countOwnedTokensForChain(cid);

          if (newCount === prevCount) {
            stableRounds++;
          } else {
            stableRounds = 0;
          }

          prevCount = newCount;

          // Petite pause pour ne pas bourriner la RPC
          await new Promise(res => setTimeout(res, 15000));
        }

        console.log(
          `[TPC][bg] ✅ balances stable for chainId=${cid} (ownedTokens=${prevCount})`,
        );
      }
    } finally {
      this._bgCompletionRunning[key] = false;
    }
  }

  // ─────────────────────  API publique  ─────────────────────

  async warmUp(address: string, startChainId: number, allChainIds: number[] = []): Promise<void> {
    const addr = toLowerAddr(address);
    const startCid = Number(startChainId);

    const uniqueIds = Array.from(
      new Set<number>([startCid, ...allChainIds.map(c => Number(c))]),
    );

    dbg(
      `🔥 [TPC][warmUp][phase1] addr=${addr} startChainId=${startCid} chainIds=${JSON.stringify(
        uniqueIds,
      )}`,
    );

    for (const cid of uniqueIds) {
      try {
        await this.ensureTokens(cid);
        await this.ensureBalances(addr, cid);
      } catch (e) {
        console.warn(`[TPC][warmUp] ❌ error on chainId=${cid}`, e);
      }
    }

    void this.runBackgroundCompletion(addr, uniqueIds);
    dbg(`[TPC][warmUp][phase1] ✅ done for addr=${addr}`);
  }

  userTokens$(chainId: number): Observable<Token[]> {
    const cid = Number(chainId);
    dbg(`[TPC][userTokens$] subscribe chainId=${cid}`);

    if (!this.tokensBus.has(cid)) {
      this.tokensBus.set(cid, new BehaviorSubject<Token[]>(this.tokensByChain.get(cid) || []));
      dbg(`[TPC][userTokens$] chainId=${cid} (no auto-refresh, cache only)`);
    }
    return this.tokensBus.get(cid)!.asObservable().pipe(shareReplay(1));
  }

  tokenBalances$(address: string, chainId: number): Observable<Record<string, string>> {
    const addr = toLowerAddr(address);
    const cid = Number(chainId);
    const aggKey = this.batchKey(addr, cid);

    dbg(`[TPC][tokenBalances$] subscribe addr=${addr} chainId=${cid}`);

    let bus = this.balancesAggBus.get(aggKey);

    if (!bus) {
      bus = new BehaviorSubject<Record<string, string>>({});
      this.balancesAggBus.set(aggKey, bus);

      const snap: Record<string, string> = {};
      for (const [k, v] of this.balances.entries()) {
        if (k.endsWith(`_${cid}`)) {
          snap[k] = v;
        }
      }
      if (Object.keys(snap).length) {
        dbg(
          `[TPC][emit][balances][snapshot:first] addr=${addr} chainId=${cid} count=${Object.keys(
            snap,
          ).length}`,
        );
        bus.next(snap);
      }

      dbg(`[TPC][tokenBalances$] addr=${addr} chainId=${cid} (no auto ensureBalances)`);
    } else {
      const snap: Record<string, string> = {};
      for (const [k, v] of this.balances.entries()) {
        if (k.endsWith(`_${cid}`)) {
          snap[k] = v;
        }
      }
      if (Object.keys(snap).length) {
        dbg(
          `[TPC][emit][balances][snapshot:resub] addr=${addr} chainId=${cid} count=${Object.keys(
            snap,
          ).length}`,
        );
        bus.next(snap);
      } else {
        dbg(
          `[TPC][emit][balances][snapshot:resub] addr=${addr} chainId=${cid} (empty snapshot)`,
        );
      }
    }

    return this.balancesAggBus.get(aggKey)!.asObservable().pipe(shareReplay(1));
  }

  ownedTokens$(address: string, chainId: number): Observable<Token[]> {
    const addr = toLowerAddr(address);
    const cid = Number(chainId);
    const out = new BehaviorSubject<Token[]>([]);
    dbg(`[TPC][ownedTokens$] subscribe addr=${addr} chainId=${cid}`);

    const subA = this.userTokens$(cid).subscribe(() => this.recomputeOwned(addr, cid, out));
    const subB = this.tokenBalances$(addr, cid).subscribe(() => this.recomputeOwned(addr, cid, out));

    (out as any).__kill = () => {
      subA.unsubscribe();
      subB.unsubscribe();
    };
    return out.asObservable().pipe(shareReplay(1));
  }

  async forceRefresh(address: string, chainId: number): Promise<void> {
    const addr = toLowerAddr(address);
    const cid = Number(chainId);
    dbg(`♻️ [TPC][forceRefresh] addr=${addr} chainId=${cid} (no TTL → full rescan)`);
    await this.ensureBalances(addr, cid);
  }

  async ensure(chainId: number): Promise<void> {
    await this.ensureTokens(chainId);
  }

  clearAll(): void {
    dbg('🧼 [TPC][clearAll] all caches & buses');
    this.tokensByChain.clear();
    this.tokensStamp.clear();
    this.balances.clear();
    this.balancesStamp.clear();

    for (const b of this.tokensBus.values()) b.complete();
    for (const b of this.balancesAggBus.values()) b.complete();

    this.tokensBus.clear();
    this.balancesAggBus.clear();
  }

  // ─────────────────────  cœur tokens + balances  ─────────────────────

  private async ensureTokens(chainId: number): Promise<void> {
    const cid = Number(chainId);

    if ((this.tokensByChain.get(cid)?.length || 0) > 0) {
      dbg(`[TPC][ensureTokens] chainId=${cid} ✅ cache already loaded (no TTL)`);
      return;
    }

    dbg(`[TPC][ensureTokens] chainId=${cid} ⏳ loading tokens (first fill)...`);
    try {
      const tokens = await this.fetchTokensForChain(cid);
      const normalized = this.normalizeTokens(tokens, cid);
      const deduped = this.dedupTokens(normalized);

      dbg(
        `[TPC][tokens] chainId=${cid} loaded=${tokens.length} normalized=${normalized.length} deduped=${deduped.length}`,
      );

      this.tokensByChain.set(cid, deduped);
      this.getTokensBus(cid).next(deduped);
      dbg(`[TPC][emit][userTokens] chainId=${cid} count=${deduped.length}`);
    } catch (e) {
      console.warn(`[TPC][ensureTokens] chainId=${cid} ❌ error (no TTL)`, e);
    }
  }

  private async ensureBalances(address: string, chainId: number): Promise<void> {
    const addr = toLowerAddr(address);
    const cid = Number(chainId);
    const aggKey = this.batchKey(addr, cid);

    dbg(
      `[TPC][ensureBalances] addr=${addr} chainId=${cid} ⏳ full scan (no TTL) aggKey=${aggKey}`,
    );

    if (!this.tokensByChain.get(cid)?.length) {
      dbg(
        `[TPC][ensureBalances] addr=${addr} chainId=${cid} (no tokens yet) → ensureTokens & retry`,
      );
      await this.ensureTokens(cid);
    }

    const allTokens = this.tokensByChain.get(cid) || [];
    if (!allTokens.length) {
      dbg(`[TPC][ensureBalances] addr=${addr} chainId=${cid} ❌ still no tokens, abort`);
      return;
    }

    let ownedAddresses: string[] = [];
    try {
      ownedAddresses = await this.wallet.getOwnedTokensRaw(addr, cid);
    } catch (e) {
      console.warn(
        `[TPC][ensureBalances] addr=${addr} chainId=${cid} ❌ getOwnedTokensRaw failed`,
        e,
      );
    }
    const ownedSet = new Set(ownedAddresses.map(toLowerAddr));

    const targetTokens: Token[] = [];
    let nativeToken: Token | undefined;

    for (const t of allTokens) {
      const tAddr = toLowerAddr(t.address);

      if (!t.address || tAddr === ZERO_ADDR) {
        nativeToken = t;
        continue;
      }

      if (ownedSet.has(tAddr)) {
        targetTokens.push(t);
      }
    }

    if (nativeToken && !targetTokens.includes(nativeToken)) {
      targetTokens.unshift(nativeToken);
    }

    if (!targetTokens.length) {
      dbg(
        `[TPC][ensureBalances] addr=${addr} chainId=${cid} ⚠️ no owned/native tokens to scan (no TTL, no stamp)`,
      );
      return;
    }

    dbg(
      `[TPC][ensureBalances][phase1] addr=${addr} chainId=${cid} totalTokens=${allTokens.length} targetTokens=${targetTokens.length}`,
    );

    await this.fillBalancesFromBackend(addr, cid, targetTokens);

    dbg(
      `[TPC][ensureBalances][phase1] addr=${addr} chainId=${cid} totalTokens=${allTokens.length} targetTokens=${targetTokens.length} (no TTL)`,
    );
  }

  private async fillBalancesFromBackend(
    addr: string,
    cid: number,
    tokens: Token[],
  ): Promise<void> {
    const bus = this.getBalancesBus(addr, cid);

    dbg(
      `[TPC][fillBalancesFromBackend] addr=${addr} chainId=${cid} count=${tokens.length} maxParallel=${this.MAX_PARALLEL_BAL_CALLS}`,
    );

    if (!tokens.length) return;

    let index = 0;
    let pushed = 0;

    const worker = async () => {
      while (index < tokens.length) {
        const t = tokens[index++];
        const tokenAddr = toLowerAddr(t.address || ZERO_ADDR);
        const k = balKey(cid, tokenAddr);

        try {
          const balanceStr = await this.swapApi.getBalance(
            {
              address: t.address,
              chainId: t.chainId,
              symbol: t.symbol,
              name: t.name,
              decimals: t.decimals,
            },
            addr,
            cid,
          );

          const prev = this.balances.get(k);
          if (prev !== balanceStr) {
            this.balances.set(k, balanceStr);
            const patch: Record<string, string> = { [k]: balanceStr };
            bus.next(patch);
            pushed++;
            dbg(`[TPC][emit][balance] key=${k} = ${balanceStr}`);
          }
        } catch (e: any) {
          console.warn(`[TPC][fillBalancesFromBackend] ❌ key=${k}`, e?.message || e);
        }
      }
    };

    const workers: Promise<void>[] = [];
    const concurrency = Math.min(this.MAX_PARALLEL_BAL_CALLS, tokens.length);
    for (let i = 0; i < concurrency; i++) {
      workers.push(worker());
    }
    await Promise.all(workers);

    dbg(`[TPC][fillBalancesFromBackend] addr=${addr} chainId=${cid} pushed=${pushed}`);
  }

  private getTokensBus(chainId: number): BehaviorSubject<Token[]> {
    const cid = Number(chainId);
    if (!this.tokensBus.has(cid)) {
      this.tokensBus.set(cid, new BehaviorSubject<Token[]>(this.tokensByChain.get(cid) || []));
    }
    return this.tokensBus.get(cid)!;
  }

  private getBalancesBus(addr: string, chainId: number): BehaviorSubject<Record<string, string>> {
    const aggKey = this.batchKey(addr, chainId);
    if (!this.balancesAggBus.has(aggKey)) {
      this.balancesAggBus.set(aggKey, new BehaviorSubject<Record<string, string>>({}));
    }
    return this.balancesAggBus.get(aggKey)!;
  }

  private batchKey(addr: string, chainId: number): string {
    return `${toLowerAddr(addr)}__${Number(chainId)}`;
  }

  private normalizeTokens(tokens: Token[], cid: number): Token[] {
    return (tokens || [])
      .filter(t => !!t?.address)
      .map(t => ({
        address: toLowerAddr(t.address),
        chainId: Number(t.chainId ?? cid),
        symbol: (t.symbol || '').toUpperCase(),
        name: t.name || '',
        decimals: typeof t.decimals === 'number' ? t.decimals : undefined,
        logoURI: (t as any).logoURI || undefined,
      }))
      .sort((a, b) => (a.symbol || '').localeCompare(b.symbol || ''));
  }

  private dedupTokens(list: Token[]): Token[] {
    const seen = new Set<string>();
    const out: Token[] = [];
    for (const t of list) {
      const key = `${toLowerAddr(t.address)}_${Number(t.chainId)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(t);
    }
    return out;
  }

  private recomputeOwned(addr: string, cid: number, out: BehaviorSubject<Token[]>) {
    const tokens = this.tokensByChain.get(cid) || [];
    const owned: Token[] = [];

    for (const t of tokens) {
      const tokenAddr = toLowerAddr(t.address || ZERO_ADDR);
      const k = balKey(cid, tokenAddr);
      const v = this.balances.get(k);
      if (!v) continue;
      if (this.isGtZero(v)) owned.push(t);
    }

    dbg(`[TPC][owned] addr=${addr} chainId=${cid} count=${owned.length}`);
    out.next(owned);
  }

  private isGtZero(amountStr: string): boolean {
    if (!amountStr) return false;
    return !/^0(?:\.0+)?$/.test(amountStr);
  }

  private async fetchTokensForChain(chainId: number): Promise<Token[]> {
    const cid = Number(chainId);
    dbg(`[TPC][fetchTokensForChain] chainId=${cid} via swapApi.getAvailableTokens()`);

    const all = await firstValueFrom(this.swapApi.getAvailableTokens());
    const raw = (all || []).filter((t: any) => Number(t?.chainId) === cid);

    const norm: Token[] = raw
      .filter((t: any) => !!t?.address)
      .map((t: any) => {
        let addr = String(t.address).toLowerCase();
        if (addr === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee') {
          addr = ZERO_ADDR;
        }
        const logo =
          t.logoURI || t.logoUrl || t.logo_url || t.logo || t.image || t.icon || '';
        return {
          address: addr,
          chainId: cid,
          symbol: String(t.symbol || '').toUpperCase(),
          name: t.name || '',
          decimals: typeof t.decimals === 'number' ? t.decimals : undefined,
          logoURI: logo || undefined,
        } as Token;
      });

    const hasNative = norm.some(tt => !tt.address || tt.address.toLowerCase() === ZERO_ADDR);
    if (!hasNative) {
      const native = this.nativeTokenForChain(cid);
      if (native) norm.unshift(native);
    }

    return norm;
  }

  private nativeTokenForChain(chainId: number): Token | null {
    const cid = Number(chainId);
    switch (cid) {
      case 1:
        return {
          address: ZERO_ADDR,
          chainId: cid,
          symbol: 'ETH',
          name: 'Ether',
          decimals: 18,
          logoURI:
            'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/info/logo.png',
        };
      case 137:
        return {
          address: ZERO_ADDR,
          chainId: cid,
          symbol: 'MATIC',
          name: 'Polygon',
          decimals: 18,
          logoURI:
            'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/polygon/info/logo.png',
        };
      case 56:
        return {
          address: ZERO_ADDR,
          chainId: cid,
          symbol: 'BNB',
          name: 'BNB',
          decimals: 18,
          logoURI:
            'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/binance/info/logo.png',
        };
      case 42161:
        return {
          address: ZERO_ADDR,
          chainId: cid,
          symbol: 'ETH',
          name: 'Arbitrum',
          decimals: 18,
          logoURI:
            'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/arbitrum/info/logo.png',
        };
      case 10:
        return {
          address: ZERO_ADDR,
          chainId: cid,
          symbol: 'ETH',
          name: 'Optimism',
          decimals: 18,
          logoURI:
            'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/optimism/info/logo.png',
        };
      case 8453:
        return {
          address: ZERO_ADDR,
          chainId: cid,
          symbol: 'ETH',
          name: 'Base',
          decimals: 18,
          logoURI:
            'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/base/info/logo.png',
        };
      case 43114:
        return {
          address: ZERO_ADDR,
          chainId: cid,
          symbol: 'AVAX',
          name: 'Avalanche',
          decimals: 18,
          logoURI:
            'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/avalanchec/info/logo.png',
        };
      default:
        return null;
    }
  }
}
